import { useCallback, useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Camera, ArrowCounterClockwise, Trash, CheckCircle, XCircle } from "@phosphor-icons/react";
import { Photo } from "@/components/Photo";
import { StudentAvatar } from "@/components/StudentAvatar";
import { isAdmin, isParent } from "@/lib/roles";

export const PhotosTab = ({ student, user, onChanged, onUpload }) => {
  const [photos, setPhotos] = useState([]);
  const [hist, setHist] = useState(null);

  const load = useCallback(async () => {
    try {
      const reqs = [api.get("/photos", { params: { student_id: student.id } })];
      if (isAdmin(user)) reqs.push(api.get(`/students/${student.id}/photo-history`));
      const [p, h] = await Promise.all(reqs);
      setPhotos(p.data); if (h) setHist(h.data);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  }, [student.id, user]);
  useEffect(() => { load(); }, [load]);

  const review = async (approve) => {
    const reason = approve ? "" : window.prompt("Reason for rejecting this photo?") || "";
    try { await api.post(`/students/${student.id}/photo/review`, { approve, reason }); toast.success(approve ? "Photo approved" : "Photo rejected"); onChanged(); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const restore = async () => {
    try { await api.post(`/students/${student.id}/photo/restore`); toast.success("Previous photo restored"); onChanged(); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const remove = async () => {
    if (!window.confirm("Remove the current profile photo?")) return;
    try { await api.delete(`/students/${student.id}/photo`); toast.success("Photo removed"); onChanged(); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-4" data-testid="photos-tab">
      <div className="bg-white rounded-2xl border border-border p-6 flex flex-wrap gap-6 items-center" data-testid="profile-photo-panel">
        <StudentAvatar student={student} size="xl" testid="photos-current" />
        <div className="flex-1 min-w-[14rem]">
          <h2 className="font-display text-lg font-semibold">Profile photograph</h2>
          <p className="text-sm text-muted-foreground mt-1">Photos are stored privately and served only to authorized accounts — never at a guessable public link.</p>
          <div className="mt-4 flex flex-wrap gap-2">
            <Button size="sm" onClick={onUpload} className="rounded-full bg-[#3B82F6] hover:bg-[#2563EB] font-bold" data-testid="upload-photo-btn"><Camera size={15} className="mr-1" /> {student.photo_path ? "Replace" : "Add photo"}</Button>
            {student.photo_path && <Button size="sm" variant="outline" className="rounded-full font-bold text-[#EF4444]" onClick={remove} data-testid="remove-photo-btn"><Trash size={15} className="mr-1" /> Remove</Button>}
            {isAdmin(user) && hist?.previous && <Button size="sm" variant="outline" className="rounded-full font-bold" onClick={restore} data-testid="restore-photo-btn"><ArrowCounterClockwise size={15} className="mr-1" /> Restore previous</Button>}
          </div>
        </div>
        {student.pending_photo_path && !isParent(user) && (
          <div className="rounded-2xl border-2 border-dashed border-amber-300 p-4 text-center" data-testid="pending-photo">
            <Photo path={student.pending_photo_path} alt="Pending photo" className="h-24 w-24 rounded-2xl object-cover mx-auto" testid="pending-photo-img" />
            <p className="mt-2 text-xs font-bold text-amber-700">Awaiting approval</p>
            {isAdmin(user) && <div className="mt-2 flex gap-1 justify-center">
              <Button size="sm" className="rounded-full bg-emerald-500 hover:bg-emerald-600 h-8" onClick={() => review(true)} data-testid="approve-photo-btn"><CheckCircle size={15} /></Button>
              <Button size="sm" variant="outline" className="rounded-full h-8 text-[#EF4444]" onClick={() => review(false)} data-testid="reject-photo-btn"><XCircle size={15} /></Button></div>}
          </div>
        )}
        {student.pending_photo_path && isParent(user) && <p className="text-xs font-bold text-amber-700" data-testid="pending-photo-note">Your new photo is waiting for approval.</p>}
      </div>

      {hist?.history?.length > 0 && (
        <div className="bg-white rounded-2xl border border-border p-6" data-testid="photo-history">
          <h2 className="font-display text-lg font-semibold">Photo change history</h2>
          <ul className="mt-3 divide-y divide-border text-sm">
            {hist.history.slice().reverse().map((h, i) => <li key={`${h.at}-${i}`} className="py-2 flex justify-between gap-2"><span>{h.by} <span className="text-muted-foreground">({h.role})</span></span><span className="text-muted-foreground">{h.at.slice(0, 16).replace("T", " ")} · {h.status}</span></li>)}
          </ul>
        </div>
      )}

      <div className="bg-white rounded-2xl border border-border p-6" data-testid="classroom-photos">
        <h2 className="font-display text-lg font-semibold">Classroom photo moments</h2>
        {photos.length === 0 && <p className="mt-3 text-sm text-muted-foreground">No classroom photos shared yet.</p>}
        <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-3">
          {photos.map((p) => (
            <figure key={p.id} data-testid={`moment-${p.id}`}>
              <Photo path={p.photo_path} alt={p.detail || "Classroom photo"} className="rounded-2xl w-full h-36 object-cover" testid={`moment-img-${p.id}`} />
              <figcaption className="mt-1 text-xs text-muted-foreground">{p.date}{p.detail ? ` · ${p.detail}` : ""}{p.staff_only ? " · staff only" : ""}</figcaption>
            </figure>
          ))}
        </div>
      </div>
    </div>
  );
};
